import {
	GatewayAuthenticationType,
	GatewayEdition,
	GatewayInfo,
	ServerInfo,
} from '../../../Common/Definitions';
import { Logger } from '../../utils/Logger';
import { logConstants } from '../../../utils/Constants';
import urlUtils from '../../utils/urlUtils';
import { HttpHeaders, HttpResponse } from '../../../utils/http/Http';
import { HttpStatusCodes } from '../../../utils/http/HttpConstants';
import {
	CLOUD_HEADER_NAME,
	SELF_DECLARED_AUTH_TYPE_HEADER,
	ACCEPT_ROAMINGACCOUNTS_RESPONSE,
} from '../../Constants';
import * as setCookieParser from 'set-cookie-parser';
import { headerConstants } from '../../../auth/AuthConstants';
import { HeadersJson } from '../../../auth/dsAuth/data/AuthTypes';
import {
	AccountConfigError,
	AccountConfigErrorStrings,
	ParserErrorStrings,
} from '../../Errors';
import {
	getRoamingAccountsResponse,
	getDiscoverProbeResponse,
	getIndexProbeResponse,
	getCgiAuthResponse,
} from './ResponseFetch';
import { StoreType } from '../../../Common/TypeDefinitions';

/**
 * * Function that determines the server type of a given store URL, if store is valid
 * * None of the network requests in this function should include any cookies
 * @param {string} url - Store URL
 * @returns {ServerInfo} - Server type information
 */
export async function determineServerType(url: string): Promise<{
	serverInfo: ServerInfo;
	roamingAccountsResponse: HttpResponse | null;
	redirectUrlFromRoamingAccounts: string | null;
}> {
	const serverInfo = new ServerInfo();

	try {
		const serverUrl = new URL(url);

		const { roamingAccountsResponse, redirectUrlFromRoamingAccounts } =
			await getRoamingAccountsResponse(url);

		if (isCloudAccount(roamingAccountsResponse)) {
			serverInfo.serverType = StoreType.Cloud;
		} else if (isStoreFrontAccount(roamingAccountsResponse)) {
			serverInfo.serverType = StoreType.StoreFront;
		} else if (await doesDiscoveryProbeResponseIndicateGateway(url)) {
			await probeForGatewayDetails(serverUrl, serverInfo);
		}
		return { serverInfo, roamingAccountsResponse, redirectUrlFromRoamingAccounts };
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			`${url}: Determine server type failure with error - ${e.message}`
		);
		if (e instanceof AccountConfigError) {
			throw e;
		}
	}
	return {
		serverInfo,
		roamingAccountsResponse: null,
		redirectUrlFromRoamingAccounts: null,
	};
}

/**
 * * Given roaming accounts response, this function returns if it's a cloud account or not
 * @param {HttpResponse} roamingAccountsResponse - Roaming accounts response
 * @returns {boolean}
 */
export function isCloudAccount(roamingAccountsResponse: HttpResponse): boolean {
	return (
		roamingAccountsResponse &&
		roamingAccountsResponse.statusCode === HttpStatusCodes.HTTP_STATUS_OK &&
		roamingAccountsResponse.headers.get(CLOUD_HEADER_NAME) != undefined &&
		roamingAccountsResponse.headers.get('Content-Type')?.toLowerCase() ==
			ACCEPT_ROAMINGACCOUNTS_RESPONSE.toLowerCase()
	);
}

/**
 * * Given roaming accounts response, this function returns if it's a storefront account or not
 * @param {HttpResponse} roamingAccountsResponse - Roaming accounts response
 * @returns {boolean}
 */
export function isStoreFrontAccount(roamingAccountsResponse: HttpResponse): boolean {
	return (
		roamingAccountsResponse &&
		roamingAccountsResponse.statusCode === HttpStatusCodes.HTTP_STATUS_OK &&
		roamingAccountsResponse.headers.get(CLOUD_HEADER_NAME) === undefined &&
		roamingAccountsResponse.body != undefined &&
		roamingAccountsResponse.headers.get('Content-Type')?.toLowerCase() ==
			ACCEPT_ROAMINGACCOUNTS_RESPONSE.toLowerCase()
	);
}

/**
 * * Function that checks if a given store is a gateway or not
 * @param {string} url - Store URL
 * @returns {boolean}
 */
async function doesDiscoveryProbeResponseIndicateGateway(url: string): Promise<boolean> {
	try {
		const serverUrl = new URL(url);
		const discoverProbeResponse = await getDiscoverProbeResponse(serverUrl);

		if (discoverProbeResponse.statusCode !== HttpStatusCodes.HTTP_STATUS_FORBIDDEN)
			return false;

		if (discoverProbeResponse.headers.get('X-Citrix-Session-Expired') === undefined)
			return false;

		return true;
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			`${url}: Discover probing failure - ${e.message}`
		);
		throw e;
	}
}

/**
 * * Function that fetches and updates gateway server information
 * @param {URL} serverUrl - Store URL
 * @param {ServerInfo} serverInfo - ref ServerInfo - server information
 * @returns {Promise<void>}
 */
async function probeForGatewayDetails(
	serverUrl: URL,
	serverInfo: ServerInfo
): Promise<void> {
	serverInfo.gatewayInfo = new GatewayInfo();

	const cgiAuthResponse = await getCgiAuthResponse(serverUrl);

	if (cgiAuthResponse.statusCode === HttpStatusCodes.HTTP_STATUS_OK) {
		serverInfo.gatewayInfo.AuthenticationType =
			GatewayAuthenticationType.Gateway_AuthType_Domain_And_RSA;
		serverInfo.serverType = StoreType.AdvancedGateway;
		serverInfo.gatewayInfo.Edition = GatewayEdition.Gateway_Edition_Enterprise;
		serverInfo.gatewayInfo.LogonPointUrl = urlUtils.addPathToUrl(serverUrl.origin, '/');
	} else if (cgiAuthResponse.statusCode === HttpStatusCodes.HTTP_STATUS_REDIRECT) {
		await getGatewayPasswordCountAndSelfDeclaredAuthType(serverUrl, serverInfo);
		serverInfo.serverType = StoreType.BasicGateway;
		serverInfo.gatewayInfo.Edition = GatewayEdition.Gateway_Edition_Enterprise;
		serverInfo.gatewayInfo.LogonPointUrl = urlUtils.addPathToUrl(serverUrl.origin, '/');
	} else return;
}

/**
 * * Function that fetches and updates pwcount and self-declared auth type in server information
 * @param {URL} serverUrl - Store URL
 * @param {ServerInfo} serverInfo - ref ServerInfo - server information
 * @returns {Promise<void>}
 */
export async function getGatewayPasswordCountAndSelfDeclaredAuthType(
	serverUrl: URL,
	serverInfo: ServerInfo
): Promise<void> {
	try {
		const indexResponse = await getIndexProbeResponse(serverUrl);

		let pwcount = 2;
		let selfDeclaredAuthType;

		if (indexResponse.statusCode === HttpStatusCodes.HTTP_STATUS_REDIRECT) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${serverUrl}: Index response returned redirect, ignoring`
			);
			return;
		}

		if (indexResponse.statusCode === HttpStatusCodes.HTTP_STATUS_OK) {
			const pwcountCookie = extractCookieFromResponseHeaders(
				indexResponse.headers,
				'pwcount'
			);

			if (pwcountCookie) {
				if (pwcountCookie.value == '0') pwcount = 1;
				else if (pwcountCookie.value == '2') pwcount = 2;
				else {
					Logger.getInstance().write(
						logConstants.ERROR,
						`${serverUrl}: Unexpected cookie value receiver for pwcount - ${pwcountCookie.value}`
					);
					throw new Error(AccountConfigErrorStrings.UNEXPECTED_COOKIE_VALUE);
				}
			}

			selfDeclaredAuthType = extractSelfDeclaredAuthType(indexResponse);
		} else {
			throw new Error(AccountConfigErrorStrings.VPN_INDEX_PROBE_FETCH_FAILURE);
		}

		serverInfo.gatewayInfo.pwcount = pwcount;

		if (selfDeclaredAuthType)
			serverInfo.gatewayInfo.AuthenticationType = selfDeclaredAuthType;
		else
			serverInfo.gatewayInfo.AuthenticationType =
				pwcount == 1
					? GatewayAuthenticationType.Gateway_AuthType_Domain
					: GatewayAuthenticationType.Gateway_AuthType_Domain_And_RSA;
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			`${serverUrl}: Fetching pwcount and self-declared auth type failed - ${e.message}`
		);
		throw e;
	}
}

/**
 * * Extracts a given cookie from response headers
 * @param {HttpHeaders} responseHeaders - response headers
 * @param {string} cookieName - cookie name
 * @returns {setCookieParser.Cookie}
 */
export function extractCookieFromResponseHeaders(
	responseHeaders: HttpHeaders | HeadersJson,
	cookieName: string
): setCookieParser.Cookie {
	let response;
	if (responseHeaders instanceof HttpHeaders) {
		response = responseHeaders.toJSON();
	} else {
		response = responseHeaders;
	}

	const cookieHeaderValue = extractHeaderValue(
		response,
		headerConstants.SET_COOKIE_HEADER
	);

	if (cookieHeaderValue) {
		const unparsedCookies: string[] =
			setCookieParser.splitCookiesString(cookieHeaderValue);
		const cookies: setCookieParser.Cookie[] = setCookieParser.parse(unparsedCookies, {
			decodeValues: false,
		});

		for (const cookie of cookies) {
			if (cookie.name) {
				cookie.name = cookie.name.trim();
				if (cookie.name == cookieName) return cookie;
			}
		}
	}
}

/**
 * * Extracts a given header from response headers
 * @param {HeadersJson} headers - response headers
 * @param {string} headerKey - header name that is to be extracted
 * @returns {string}
 */
function extractHeaderValue(headers: HeadersJson, headerKey: string): string {
	for (const key in headers) {
		if (key.toLowerCase() === headerKey.toLowerCase()) {
			return headers[key];
		}
	}
}

/**
 * * Extracts self-declared auth type from response headers if present
 * @param {HttpResponse} response - response headers
 * @returns {GatewayAuthenticationType}
 */
function extractSelfDeclaredAuthType(response: HttpResponse): GatewayAuthenticationType {
	try {
		if (response.headers.get(SELF_DECLARED_AUTH_TYPE_HEADER) === undefined)
			return undefined;

		if (
			Object.values(GatewayAuthenticationType).includes(
				response.headers.get(SELF_DECLARED_AUTH_TYPE_HEADER) as GatewayAuthenticationType
			)
		)
			return response.headers.get(
				SELF_DECLARED_AUTH_TYPE_HEADER
			) as GatewayAuthenticationType;

		throw new Error(ParserErrorStrings.SELF_DECLARED_AUTH_TYPE_PARSE_ERROR);
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			`Exception while extracting self-declared authentication type - ${response.headers}`
		);
		throw e;
	}
}
